import type { Connector } from '../../../util/PostMessageConnector';
import type { ApiInitArgs, OnApiUpdate } from '../../types';
import type {
  AllMethods,
  MethodArgsWithMaybePrefix,
  MethodResponseWithMaybePrefix,
} from '../../types/methods';

import { logDebugApi, logDebugError } from '../../../util/logs';
import { createConnector, createExtensionConnector } from '../../../util/PostMessageConnector';
import { pause } from '../../../util/schedulers';
import { IS_IOS } from '../../../util/windowEnvironment';
import { createWindowProvider, createWindowProviderForExtension } from '../../../util/windowProvider';
import { POPUP_PORT } from '../extension/config';

const HEALTH_CHECK_TIMEOUT = 150;
// How long a worker is allowed to spend booting before silence counts as death rather than
// as a slow start. Measured from the moment THIS worker was created.
const WORKER_BOOT_BUDGET = 5000; // 5 sec

let updateCallback: OnApiUpdate;
let worker: Worker | undefined;
let connector: Connector | undefined;
let isInitialized = false;
let initPromise: Promise<void> | undefined;
let isHealthCheckRunning = false;

/**
 * What the health check knows about the worker currently in place. Readiness lives HERE rather
 * than in a module flag so it cannot outlive the worker it describes: a retired worker's `init`
 * settling late mutates the record nobody reads any more, instead of vouching for its successor.
 */
type WorkerState = { createdAt: number; isReady: boolean };

let workerState: WorkerState = { createdAt: 0, isReady: false };

export function initApi(onUpdate: OnApiUpdate, initArgs: ApiInitArgs) {
  updateCallback = onUpdate;

  if (!connector) {
    // We use process.env.IS_EXTENSION instead of IS_EXTENSION in order to remove the irrelevant code during bundling
    if (process.env.IS_EXTENSION) {
      const onReconnect = () => {
        initPromise = trackReadiness(connector!.init(initArgs));
      };

      connector = createExtensionConnector(POPUP_PORT, onUpdate, undefined, onReconnect);

      createWindowProviderForExtension();
    } else {
      worker = new Worker(
        /* webpackChunkName: "worker" */ new URL('./provider.ts', import.meta.url),
      );
      workerState = { createdAt: Date.now(), isReady: false };
      connector = createConnector(worker, onUpdate);

      createWindowProvider(worker);
    }
  }

  if (!isInitialized) {
    if (IS_IOS) {
      setupIosHealthCheck();
    }
    isInitialized = true;
  }

  initPromise = trackReadiness(connector.init(initArgs));
}

/**
 * A worker counts as ready once its `init` has settled. Until then it cannot answer a ping,
 * and the health check must not read that silence as death.
 */
function trackReadiness(promise: Promise<void>) {
  const state = workerState;

  void promise.then(
    () => { state.isReady = true; },
    () => undefined,
  );

  return promise;
}

export async function callApi<T extends keyof AllMethods>(
  fnName: T,
  ...args: MethodArgsWithMaybePrefix<T>
) {
  if (!connector) {
    logDebugError('API is not initialized when calling', fnName);
    return undefined;
  }

  await initPromise!;

  try {
    const result = await (connector.request({
      name: fnName,
      args,
    }) as Promise<MethodResponseWithMaybePrefix<T>>);

    logDebugApi(`callApi: ${fnName}`, args, result);

    return result;
  } catch (err) {
    // Callers treat `undefined` as a transport failure, so record the swallowed cause for support logs.
    // Args are deliberately not logged: they may carry sensitive payloads.
    logDebugError(`callApi: ${fnName}`, err);
    return undefined;
  }
}

export async function callApiWithThrow<T extends keyof AllMethods>(
  fnName: T,
  ...args: MethodArgsWithMaybePrefix<T>
) {
  await initPromise!;

  return (connector!.request({
    name: fnName,
    args,
  }) as MethodResponseWithMaybePrefix<T>);
}

/**
 * Whether an unanswered ping is evidence that the worker is gone. A worker that has not
 * finished initialising is silent for a mundane reason, so silence only proves death once the
 * worker has had its whole boot budget. Exported for the test that pins this distinction.
 */
export function isSilenceConclusive(isReady: boolean, workerAgeMs: number) {
  return isReady || workerAgeMs >= WORKER_BOOT_BUDGET;
}

// Workaround for iOS sometimes stops interacting with worker
function setupIosHealthCheck() {
  window.addEventListener('focus', () => {
    void ensureWorkerPing();
    // Sometimes a single check is not enough
    setTimeout(() => ensureWorkerPing(), 1000);
  });
}

async function ensureWorkerPing() {
  // Each focus fires two checks, and a focus can arrive while an earlier one is still racing.
  // Without this, concurrent checks each terminate in turn, so one gesture destroys a chain of
  // workers instead of one.
  if (isHealthCheckRunning) {
    return;
  }

  if (!isSilenceConclusive(workerState.isReady, Date.now() - workerState.createdAt)) {
    return;
  }

  isHealthCheckRunning = true;
  let isResolved = false;

  try {
    await Promise.race([
      callApiWithThrow('ping'),
      pause(HEALTH_CHECK_TIMEOUT)
        .then(() => (isResolved ? undefined : Promise.reject(new Error('HEALTH_CHECK_TIMEOUT')))),
    ]);
  } catch (err) {
    logDebugError('ensureWorkerPing', err);

    worker?.terminate();
    worker = undefined;
    connector = undefined;
    initPromise = undefined;
    workerState = { createdAt: Date.now(), isReady: false };
    updateCallback({ type: 'requestReconnectApi' });
  } finally {
    isResolved = true;
    isHealthCheckRunning = false;
  }
}
